import throttle from '@plugins/throttle';

export default class StickyNav {

  constructor() {
    this.$element = $('#sticky-nav');

    if (!this.$element.length) {
      return;
    }

    // Get DOM objects
    this.$menu = this.$element.find('#sticky-nav-menu');
    this.$mobileButton = this.$element.find('#sticky-nav-mobile-toggle');
    this.$currentItemLink = this.$element.find('#sticky-nav-current-item');
    this.$moreButton = this.$element.find('#sticky-nav-menu-more-button');
    this.$menuLinks = this.$element.find('#sticky-nav-menu a[href^="#"]');

    // Set default values
    this._activeAnchorId = '';
    this._activeLinkTitle = '';
    this._currentItemLinkIsActive = false;

    // Initialize mobile menu toggle
    this.$mobileButton.attr('aria-controls', 'sticky-nav-menu');
    this.$mobileButton.attr('aria-expanded', 'false');
    this.$mobileButton.on('click', () => {
      this.onMobileButtonClick();
    });
    this.$mobileButton.removeAttr('hidden');

    // Reveal the current item element that we will be updating
    this.$currentItemLink.removeAttr('hidden');

    // Close the mobile menu on item click
    this.$menu.on('click', 'a', () => {
      this.hideMobileMenu();
    });

    // Initialize desktop "more" menu toggle, if present
    if (this.$moreButton.length) {
      this.$moreButton.attr('aria-controls', 'sticky-nav-menu-more-section');
      this.$moreButton.attr('aria-expanded', 'false');
      this.$moreButton.on('click', () => {
        this.onMoreButtonClick();
      });
      this.$moreButton.removeAttr('hidden');
    }

    document.addEventListener('scroll', throttle(
      () => {
        this.updateActiveOnScroll();
      },
      100
    ));
    this.updateActiveOnScroll();

    // Mark JS initialized, and hide mobile menu as initial state
    this.$element.addClass('-js-init -menu-hidden');

    // We've initialized the menu itself, but we still need to make it "sticky"
    this.$isSticky = this.$element.isSticky({
      id: 'sticky-nav-placeholder',
      class: 'sticky-nav-placeholder',
      stopper: 'footer',
      stuckClass: '-stuck',
      zIndex: 99
    });
    this.$isSticky.init();
  }

  get activeAnchorId() {
    return this._activeAnchorId;
  }

  // Expects string
  set activeAnchorId(id) {
    this.$currentItemLink.attr('href', '#' + id);
    this._activeAnchorId = id;
    this.updateActiveLinks();
  }

  get activeLinkTitle() {
    return this._activeLinkTitle;
  }

  // Expects string
  set activeLinkTitle(title) {
    this.$currentItemLink.text(title);
    this._activeLinkTitle = title;
  }

  get currentItemLinkIsActive() {
    return this._currentItemLinkIsActive;
  }

  // Expects boolean
  set currentItemLinkIsActive(isActive) {
    let isChange = false;
    if (this._currentItemLinkIsActive !== isActive) {
      isChange = true;
    }

    this._currentItemLinkIsActive = isActive;

    if (isChange) {
      if (isActive) {
        this.$currentItemLink.addClass('-active');
      } else {
        this.$currentItemLink.removeClass('-active');
      }

      this.updateActiveLinks();
    }
  }

  hideMobileMenu() {
    this.$element.addClass('-menu-hidden');
    this.$mobileButton.attr('aria-expanded', 'false');
  }

  showMobileMenu() {
    this.$element.removeClass('-menu-hidden');
    this.$mobileButton.attr('aria-expanded', 'true');
  }

  onMobileButtonClick() {
    if (this.$mobileButton.attr('aria-expanded') === 'true') {
      this.hideMobileMenu();
    } else {
      this.showMobileMenu();
    }
  }

  onMoreButtonClick() {
    this.$element.toggleClass('-menu-hidden');

    if (this.$moreButton.attr('aria-expanded') === 'true') {
      this.$moreButton.attr('aria-expanded', 'false');
    } else {
      this.$moreButton.attr('aria-expanded', 'true');
    }
  }

  updateActiveLinks() {
    this.$menuLinks.each((index, link) => {
      if (this.currentItemLinkIsActive && link.hash.slice(1) === this._activeAnchorId) {
        link.classList.add('-active');
      } else {
        link.classList.remove('-active');
      }
    });
  }

  updateActiveOnScroll() {
    const currentPos = this.$element.height() + window.scrollY;

    // Determine which of the links in the Sticky Nav should be considered
    // active right now.
    let newLink;
    for (let l = 0; l < this.$menuLinks.length; l += 1) {
      const currentLink = this.$menuLinks[l],
            currentAnchor = document.querySelector(currentLink.getAttribute('href'))
      ;

      let start,
          end
      ;

      if (l + 1 === this.$menuLinks.length) {
        // Last element. Use the <main> element's extent as the bottom limit.
        const mainElement = document.querySelector('main');
        start = currentAnchor.offsetTop;
        end = mainElement.offsetTop + mainElement.clientHeight;
      } else {
        // Standard case: define the end offset by the next anchor
        const nextLink = this.$menuLinks[l + 1],
              nextAnchor = document.querySelector(nextLink.getAttribute('href'));

        start = currentAnchor.offsetTop;
        end = nextAnchor.offsetTop - 1;
      }

      // Always set an element's parameters back 20px, to account for some of
      // the margin we will assume is there.
      // Also always set the start back 1px, to ensure absolutely no gaps / add
      // a small bit of tolerance to detect entering a section.
      start -= 21;
      end -= 20;

      // Offset by the height of the admin bar, if present
      const $adminBar = $('#wpadminbar');
      if ($adminBar.length) {
        const adminBarHeight = $adminBar.height();
        start += adminBarHeight;
        end += adminBarHeight;
      }

      if (start <= currentPos && end > currentPos) {
        newLink = currentLink;
        break;
      }
    }

    if (newLink) {
      this.currentItemLinkIsActive = true;
    } else {
      // Default to the first menu item.
      newLink = this.$menuLinks[0];
      this.currentItemLinkIsActive = false;
    }

    const newAnchor = document.querySelector(newLink.getAttribute('href'));

    if (newAnchor.id === this.activeAnchorId) {
      // No change
      return;
    }

    this.activeAnchorId = newAnchor.id;

    // Get the link text, minus any child elements such as screen reader
    // text
    let linkTitle = '';
    newLink.childNodes.forEach(function(node) {
      if (node.nodeType === Node.TEXT_NODE) {
        linkTitle += node.textContent;
      }
    });
    linkTitle = linkTitle.trim();
    this.activeLinkTitle = linkTitle;
  }

}
